// Auth bridge backed by Supabase.
//
// Mirrors the Electron auth IPC surface (auth:sign-in-email,
// auth:sign-in-google, auth:sign-out, auth:get-session) plus the
// renderer's `onAuthCallbackComplete` event. Unlike Electron — where
// OAuth round-trips a `lastslate://` deep link — the web flow is
// pure HTTP: redirect to /auth/callback, supabase-js exchanges the
// code, then we emit a callback-complete event so the renderer's
// existing handler refreshes its UI state.

const PATH_AUTH_CALLBACK = '/auth/callback';

function siteOrigin() {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

function callbackUrl() {
  return `${siteOrigin()}${PATH_AUTH_CALLBACK}`;
}

function asResult(error, payload = {}) {
  if (error) return { ok: false, error: error.message || String(error) };
  return { ok: true, ...payload };
}

// Match the Electron `auth:get-session` contract exactly so the
// renderer's `session.signedIn` / `session.user.email` reads work
// without a per-environment shim. See src/main/main.js:1119.
function sessionEnvelope(session) {
  if (!session) return { ok: true, signedIn: false };
  return {
    ok: true,
    signedIn: true,
    user: session.user,
    expiresAt: session.expires_at,
  };
}

export function buildAuthHandlers({ supabase, bus }) {
  // When the page first loads on /auth/callback, supabase-js (with
  // detectSessionInUrl: true) auto-exchanges the code, fires
  // 'SIGNED_IN' via onAuthStateChange, and clears the query string.
  // We forward that into the same `auth:callback-complete` channel
  // the Electron deep-link handler uses.
  // Remember the most recent auth state so late subscribers (the
  // renderer's onAuthCallbackComplete callback is wired inside init()
  // AFTER our bridge constructs) still see the SIGNED_IN that
  // already fired during boot.
  let lastAuthPayload = null;

  if (supabase) {
    supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
        // Only emit a "callback-complete" payload when we actually
        // have a session — the renderer treats `ok: false` here as
        // "sign-in failed" and shows an error message, which would
        // be wrong on a cold load with no session.
        lastAuthPayload = { ok: true };
        bus.emit('auth:callback-complete', lastAuthPayload);
        // Now that the exchange is done, scrub `?code=` from the URL
        // so refreshing the page doesn't try to replay an already-used
        // code. Doing this here (not earlier as a microtask) avoids a
        // race where the URL is replaced BEFORE supabase-js reads the
        // code — which was nuking the PKCE exchange on first load.
        if (window.location.pathname === PATH_AUTH_CALLBACK) {
          history.replaceState(null, '', '/');
        }
      }
      // Intentionally do NOT broadcast SIGNED_OUT through this channel.
      // The renderer's authSignOut return value already handles the
      // sign-out UI flip; routing it through callback-complete would
      // make the renderer flash "Sign-in failed".
    });
  }

  // Patch the bus's `on` so a subscriber for `auth:callback-complete`
  // attached AFTER the SIGNED_IN event still receives the current state.
  // Otherwise the renderer's late subscription silently misses the
  // initial sign-in.
  const replayChannels = new Set(['auth:callback-complete']);
  const origOn = bus.on;
  bus.on = (channel, cb) => {
    const off = origOn(channel, cb);
    if (replayChannels.has(channel) && lastAuthPayload) {
      // Defer to a microtask so the subscriber sees the event
      // asynchronously (matches EventTarget delivery semantics).
      queueMicrotask(() => cb(lastAuthPayload));
    }
    return off;
  };

  return {
    async authSignInEmail(email) {
      // The renderer calls this with a positional string; the
      // Electron preload wraps it as { email } before IPC. Accept the
      // raw string here so the contract matches.
      if (!supabase) return asResult(new Error('Supabase not configured'));
      if (!email || typeof email !== 'string') {
        return asResult(new Error('email required'));
      }
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: callbackUrl() },
      });
      return asResult(error, { sent: true });
    },

    async authSignInGoogle() {
      if (!supabase) return asResult(new Error('Supabase not configured'));
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: callbackUrl() },
      });
      // signInWithOAuth navigates the page; control returns here only
      // if Supabase fails to start the redirect.
      return asResult(error, { redirecting: true });
    },

    async authSignOut() {
      if (!supabase) return { ok: true };
      const { error } = await supabase.auth.signOut();
      return asResult(error);
    },

    async authSignInAnonymous() {
      // Mirrors src/main/auth.js: creates an anonymous Supabase user
      // so a fresh visitor can try the editor without committing to
      // a real account. Promotion to a real account happens via the
      // standard email-magic-link / Google flows above (Supabase
      // links the anon row to the new identity on sign-in).
      if (!supabase) return asResult(new Error('Supabase not configured'));
      const { data, error } = await supabase.auth.signInAnonymously();
      if (error) return asResult(error);
      return sessionEnvelope(data?.session);
    },

    // Dev-only in Electron; surfaced here so the renderer's
    // `if (typeof devGetAccessToken === 'function')` guard succeeds.
    // Returns the supabase access_token only when running on
    // localhost (any prod build serves null).
    async devGetAccessToken() {
      if (!supabase) return { ok: true, token: null };
      if (typeof location !== 'undefined' && location.hostname !== 'localhost') {
        return { ok: true, token: null };
      }
      const { data } = await supabase.auth.getSession();
      return { ok: true, token: data?.session?.access_token || null };
    },

    async authGetSession() {
      if (!supabase) return { ok: true, signedIn: false };
      const { data, error } = await supabase.auth.getSession();
      if (error) return asResult(error);
      return sessionEnvelope(data?.session);
    },
  };
}
